import axios, { AxiosInstance } from 'axios'
import { API_BASE_URL, handleAuthFailure } from '@/services/apiConfig'
import { ChatStreamBody, streamChat } from '@/services/chatStream'
import {
  User,
  Organization,
  FileItem,
  Folder,
  FolderProperties,
  FolderScope,
  SearchParams,
  SearchResult,
  SearchSuggestionsResponse,
  TagRecommendation,
  TagRecommendationResponse,
  RecentlyViewedRecord,
  SmartRenamePrompt,
  RelatedFilesResponse,
  FileLinksResponse,
  CreateFileLinkResponse,
  CreateFileLinkOptions,
  UpdateFileLinkPayload,
  UpdateFileLinkResponse,
  FileGraphResponse,
  FileNote,
  FileNotesResponse,
  FileNoteVisibility,
  Favorite,
  FolderPermission,
  FilePermission,
  Group,
  GroupMember,
  PermissionLevel,
  KeyEntity,
  AuditLogFilters,
  AuditLogPaginated,
  UsageSummary,
  UsageTimeseries,
  UsageFeatureKey,
  ChatSource,
  DuplicateCheckResponse,
  FileProcessingStatus,
} from '@/types'

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 300000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        handleAuthFailure(error.response?.status, error.response?.data, error.config?.url)
        return Promise.reject(error)
      }
    )
  }

  // Authentication
  async login(
    email: string,
    password: string
  ): Promise<{ user: User; token: string; organization: Organization }> {
    const response = await this.api.post('/login', { email, password })

    const responseData = response.data
    const { access_token: token, user } = responseData
    const organization = responseData.organization ?? user?.organization

    if (!token) {
      console.error('No token found in response:', responseData)
      throw new Error('Authentication failed: No token received')
    }

    localStorage.setItem('auth_token', token)
    localStorage.setItem('user', JSON.stringify(user))

    return {
      token,
      user,
      organization,
    }
  }

  async logout(): Promise<void> {
    await this.api.post('/logout')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
  }

  async impersonateUser(
    userId: number
  ): Promise<{ user: User; token: string; organization: Organization }> {
    const response = await this.api.post(`/user/${userId}/impersonate`)
    const { access_token: token, user } = response.data
    const organization = user?.organization

    localStorage.setItem('auth_token', token)
    localStorage.setItem('user', JSON.stringify(user))

    return { token, user, organization }
  }

  async stopImpersonation(): Promise<{ user: User; token: string; organization: Organization }> {
    const response = await this.api.delete('/user/impersonate')
    const { access_token: token, user } = response.data
    const organization = user?.organization

    localStorage.setItem('auth_token', token)
    localStorage.setItem('user', JSON.stringify(user))

    return { token, user, organization }
  }

  async refreshToken(): Promise<{ token: string; user?: User }> {
    const response = await this.api.post('/refresh')
    const { access_token, token: legacyToken, user } = response.data
    const token = access_token ?? legacyToken
    localStorage.setItem('auth_token', token)
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    }
    return { token, user }
  }

  async updatePassword(data: {
    currentPassword: string
    newPassword: string
  }): Promise<{ message: string }> {
    const response = await this.api.put('/user/password', {
      current_password: data.currentPassword,
      new_password: data.newPassword,
    })
    return response.data
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await this.api.post('/password/email', { email })
    return response.data
  }

  async resetPassword(data: {
    token: string
    email: string
    password: string
    passwordConfirmation: string
  }): Promise<{ message: string }> {
    const response = await this.api.post('/password/reset', {
      token: data.token,
      email: data.email,
      password: data.password,
      password_confirmation: data.passwordConfirmation,
    })
    return response.data
  }

  // Folders
  async getFolderTree(): Promise<Folder[]> {
    const response = await this.api.get('/folders/tree')
    return response.data
  }

  async createFolder(data: {
    name: string
    description?: string
    parent_id?: number
  }): Promise<Folder> {
    const response = await this.api.post('/folders', data)
    return response.data
  }

  async updateFolder(id: number, data: { name?: string; description?: string }): Promise<Folder> {
    const response = await this.api.put(`/folders/${id}`, data)
    return response.data
  }

  async moveFolder(id: number, parentId?: number): Promise<void> {
    await this.api.put(`/folders/${id}/move`, { parent_id: parentId })
  }

  async deleteFolder(id: number): Promise<void> {
    await this.api.delete(`/folders/${id}`)
  }

  async getFolderFiles(folderId: number): Promise<FileItem[]> {
    const response = await this.api.get(`/folders/${folderId}/files`)
    return response.data
  }

  async getRootFiles(): Promise<FileItem[]> {
    const response = await this.api.get('/folders/root/files')
    return response.data
  }

  async getFolderProperties(folderId: number): Promise<FolderProperties> {
    const response = await this.api.get(`/folders/${folderId}/properties`)
    return response.data
  }

  // Files
  async uploadFile(file: File, folderId?: number): Promise<FileItem> {
    const formData = new FormData()
    formData.append('file', file)
    if (folderId) {
      formData.append('folder_id', folderId.toString())
    }

    const response = await this.api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0,
    })
    return response.data
  }

  /**
   * Ask whether identical content already exists before spending bandwidth on it.
   * The digest is computed client-side; see utils/checksum.
   */
  async checkFileDuplicate(
    checksum: string,
    folderId?: number | null
  ): Promise<DuplicateCheckResponse> {
    const response = await this.api.post('/files/check-duplicate', {
      checksum,
      ...(folderId ? { folder_id: folderId } : {}),
    })
    return response.data
  }

  async uploadFileWithProgress(
    file: File,
    folderId?: number,
    onProgress?: (progress: number) => void,
    options?: { allowDuplicate?: boolean }
  ): Promise<FileItem> {
    const formData = new FormData()
    formData.append('file', file)
    if (folderId) {
      formData.append('folder_id', folderId.toString())
    }
    // Only sent when true — an omitted flag is unambiguously false server-side,
    // whereas the string "false" would arrive truthy through multipart.
    if (options?.allowDuplicate) {
      formData.append('allow_duplicate', '1')
    }

    const response = await this.api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
    return response.data
  }

  async uploadFolder(
    files: FileList,
    parentFolderId?: number
  ): Promise<{
    uploaded: Array<{ file: FileItem; relative_path: string; target_folder_id: number }>
    skipped: Array<{ relative_path: string; reason: string; file_name: string }>
    failed: Array<{ relative_path: string; file_name: string; error: string }>
    folders_created: number
    root_folder?: Folder
  }> {
    const formData = new FormData()
    const relativePaths: string[] = []

    Array.from(files).forEach((file) => {
      const relativePath = (file as any).webkitRelativePath || file.name
      formData.append('files[]', file)
      relativePaths.push(relativePath)
    })

    relativePaths.forEach((path) => {
      formData.append('relative_paths[]', path)
    })

    if (parentFolderId) {
      formData.append('parent_folder_id', parentFolderId.toString())
    }

    const response = await this.api.post('/files/upload-folder', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0,
    })
    return response.data
  }

  async getFile(id: number): Promise<FileItem> {
    const response = await this.api.get(`/files/${id}`)
    return response.data
  }

  async getFileText(id: number): Promise<{ text: string }> {
    const response = await this.api.get(`/files/${id}/text`)
    return response.data
  }

  async getFileStatus(
    id: number
  ): Promise<{ ocr_status: string; embedding_status: string; processing_complete: boolean }> {
    const response = await this.api.get(`/files/${id}/status`)
    return response.data
  }

  /**
   * Processing state for several files at once.
   *
   * The file list polls this while any visible row is still being processed, so it asks
   * about a whole page in one request rather than one call per file. Chunked to the
   * server's per-request cap so callers never have to think about it.
   */
  async getFileStatuses(ids: number[]): Promise<FileProcessingStatus[]> {
    if (ids.length === 0) return []

    const MAX_IDS_PER_REQUEST = 200
    const chunks: number[][] = []
    for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
      chunks.push(ids.slice(i, i + MAX_IDS_PER_REQUEST))
    }

    const responses = await Promise.all(
      chunks.map((chunk) => this.api.post('/files/statuses', { ids: chunk }))
    )

    return responses.flatMap((response) => response.data.statuses ?? [])
  }

  async getRelatedFiles(
    id: number,
    params?: { offset?: number; limit?: number }
  ): Promise<RelatedFilesResponse> {
    const response = await this.api.get(`/files/${id}/related`, { params })
    return response.data
  }

  async getFileLinks(id: number): Promise<FileLinksResponse> {
    const response = await this.api.get(`/files/${id}/links`)
    return response.data
  }

  async createFileLink(
    sourceFileId: number,
    targetFileId: number,
    options?: CreateFileLinkOptions
  ): Promise<CreateFileLinkResponse> {
    const response = await this.api.post(`/files/${sourceFileId}/links`, {
      target_file_id: targetFileId,
      ...options,
    })
    return response.data
  }

  async updateFileLink(
    fileId: number,
    linkId: number,
    payload: UpdateFileLinkPayload
  ): Promise<UpdateFileLinkResponse> {
    const response = await this.api.patch(`/files/${fileId}/links/${linkId}`, payload)
    return response.data
  }

  async deleteFileLink(fileId: number, linkId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/files/${fileId}/links/${linkId}`)
    return response.data
  }

  async getFileGraph(
    id: number,
    params?: { depth?: number; limit?: number }
  ): Promise<FileGraphResponse> {
    const response = await this.api.get(`/files/${id}/graph`, { params })
    return response.data
  }

  async moveFile(id: number, folderId?: number): Promise<void> {
    await this.api.put(`/files/${id}/move`, { folder_id: folderId })
  }

  async renameFile(id: number, displayName: string): Promise<FileItem> {
    const response = await this.api.put(`/files/${id}/rename`, { display_name: displayName })
    return response.data
  }

  async deleteFile(id: number): Promise<void> {
    await this.api.delete(`/files/${id}`)
  }

  // Search
  async searchDocuments(params: SearchParams): Promise<SearchResult> {
    const response = await this.api.get('/documents/search', { params })
    return response.data
  }

  async getSearchSuggestions(
    q: string,
    limit = 8,
    signal?: AbortSignal
  ): Promise<SearchSuggestionsResponse> {
    const response = await this.api.get('/documents/search/suggestions', {
      params: { q, limit },
      signal,
    })
    return response.data
  }

  // Tags
  async addFileTag(fileId: number, tagName: string): Promise<void> {
    await this.api.post(`/files/${fileId}/tags`, { tag_name: tagName })
  }

  async removeFileTag(fileId: number, tagName: string): Promise<void> {
    await this.api.delete(`/files/${fileId}/tags/${encodeURIComponent(tagName)}`)
  }

  async getFileTags(fileId: number): Promise<string[]> {
    const response = await this.api.get(`/files/${fileId}/tags`)
    if (Array.isArray(response.data)) {
      return response.data.map((item) =>
        typeof item === 'string' ? item : item.tag || item.tag_name || item.name
      )
    }
    return response.data
  }

  async addMultipleTagsToFile(fileId: number, tagNames: string[]): Promise<void> {
    await this.api.post(`/files/${fileId}/tags/multiple`, { tag_names: tagNames })
  }

  async getTagRecommendations(
    fileId: number,
    method = 'hybrid',
    limit = 10
  ): Promise<TagRecommendationResponse> {
    const response = await this.api.get(`/files/${fileId}/tag-recommendations`, {
      params: { method, limit },
    })
    return response.data
  }

  async generateContentTags(
    fileId: number,
    options: {
      max_tags?: number
      tag_types?: ('keywords' | 'topics' | 'categories' | 'entities' | 'document_type')[]
    } = {}
  ): Promise<{
    suggestions: TagRecommendation[]
    method: string
    source_text_length: number
    file_info: {
      name: string
      mime_type: string
      size: number
    }
  }> {
    const response = await this.api.post(`/files/${fileId}/content-tags`, options)
    return response.data
  }

  async extractKeyEntities(fileId: number): Promise<{ key_entities: KeyEntity[] }> {
    const response = await this.api.post(`/files/${fileId}/extract-entities`)
    return response.data
  }

  // Smart Renaming
  async generateSmartRenames(
    folderId: FolderScope,
    instructions: string,
    forceAsync: boolean = false,
    fileIds?: number[]
  ): Promise<{
    suggestions?: {
      file_id: number
      current_name: string
      suggested_name: string
      reasoning?: string
    }[]
    job_id?: string
    status?: string
    async: boolean
    total_files?: number
    usage_stats: { current_count: number; limit: number; remaining: number }
  }> {
    const response = await this.api.post(`/folders/${folderId}/smart-rename`, {
      instructions,
      async: forceAsync,
      ...(fileIds?.length ? { file_ids: fileIds } : {}),
    })
    return response.data
  }

  async getSmartRenameJobStatus(jobId: string): Promise<{
    job_id: string
    status: string
    progress: number
    total_files: number
    processed_files: number
    created_at: string
    completed_at?: string
    error_message?: string
  }> {
    const response = await this.api.get(`/smart-rename/jobs/${jobId}/status`)
    return response.data
  }

  async getSmartRenameJobResults(jobId: string): Promise<{
    job_id: string
    status: string
    suggestions: {
      file_id: number
      current_name: string
      suggested_name: string
      reasoning?: string
    }[]
    completed_at: string
    total_files: number
  }> {
    const response = await this.api.get(`/smart-rename/jobs/${jobId}/results`)
    return response.data
  }

  async cancelSmartRenameJob(jobId: string): Promise<{
    job_id: string
    status: string
    message: string
  }> {
    const response = await this.api.post(`/smart-rename/jobs/${jobId}/cancel`)
    return response.data
  }

  async getFolderSmartRenameJobs(folderId: FolderScope): Promise<{
    jobs: Array<{
      job_id: string
      status: string
      progress: number
      total_files: number
      processed_files: number
      instructions: string
      created_at: string
      completed_at?: string
      has_results: boolean
    }>
    latest_completed: any
  }> {
    const response = await this.api.get(`/folders/${folderId}/smart-rename/jobs`)
    return response.data
  }

  async clearFolderSmartRenameJobs(folderId: FolderScope): Promise<{
    message: string
    deleted_count: number
  }> {
    const response = await this.api.delete(`/folders/${folderId}/smart-rename/jobs`)
    return response.data
  }

  async getSmartRenamePrompts(folderId: FolderScope, limit = 10): Promise<SmartRenamePrompt[]> {
    const response = await this.api.get(`/folders/${folderId}/smart-rename/prompts`, {
      params: { limit },
    })
    return response.data
  }

  async deleteSmartRenamePrompt(folderId: FolderScope, promptId: number): Promise<void> {
    await this.api.delete(`/folders/${folderId}/smart-rename/prompts/${promptId}`)
  }

  async bulkRenameFiles(
    updates: { file_id: number; new_name: string }[]
  ): Promise<{ success: number; errors: Array<{ file_id: number; error: string }> }> {
    const response = await this.api.post('/files/bulk-rename', { updates })
    return response.data
  }

  // Smart Organize
  async generateSmartOrganize(
    folderId: FolderScope,
    instructions?: string,
    forceAsync: boolean = true,
    locale: string = 'en',
    fileIds?: number[]
  ): Promise<{
    plan?: import('@/types').SmartOrganizePlan
    job_id?: string
    status?: string
    async: boolean
    total_files?: number
    usage_stats: { current_count: number; limit: number; remaining: number }
  }> {
    const response = await this.api.post(`/folders/${folderId}/smart-organize`, {
      instructions,
      async: forceAsync,
      locale,
      ...(fileIds?.length ? { file_ids: fileIds } : {}),
    })
    return response.data
  }

  async getSmartOrganizeJobStatus(jobId: string): Promise<{
    job_id: string
    status: string
    progress: number
    total_files: number
    processed_files: number
    created_at: string
    completed_at?: string
    error_message?: string
  }> {
    const response = await this.api.get(`/smart-organize/jobs/${jobId}/status`)
    return response.data
  }

  async getSmartOrganizeJobResults(jobId: string): Promise<{
    job_id: string
    status: string
    plan: import('@/types').SmartOrganizePlan
    completed_at: string
    total_files: number
  }> {
    const response = await this.api.get(`/smart-organize/jobs/${jobId}/results`)
    return response.data
  }

  async cancelSmartOrganizeJob(jobId: string): Promise<{
    job_id: string
    status: string
    message: string
  }> {
    const response = await this.api.post(`/smart-organize/jobs/${jobId}/cancel`)
    return response.data
  }

  async getFolderSmartOrganizeJobs(folderId: FolderScope): Promise<{
    jobs: Array<{
      job_id: string
      status: string
      progress: number
      total_files: number
      processed_files: number
      instructions?: string
      created_at: string
      completed_at?: string
      has_results: boolean
    }>
    latest_completed: any
  }> {
    const response = await this.api.get(`/folders/${folderId}/smart-organize/jobs`)
    return response.data
  }

  async clearFolderSmartOrganizeJobs(folderId: FolderScope): Promise<{
    message: string
    deleted_count: number
  }> {
    const response = await this.api.delete(`/folders/${folderId}/smart-organize/jobs`)
    return response.data
  }

  async applySmartOrganize(data: {
    job_id: string
    /** null applies the plan at the top of the tree. */
    folder_id: number | null
    selected_moves: Array<{
      file_id: number
      target_folder_path: string
      new_name?: string
    }>
  }): Promise<{
    success: number
    folders_created: number
    errors: Array<{ file_id: number; error: string }>
  }> {
    const response = await this.api.post('/smart-organize/apply', data)
    return response.data
  }

  // Trash
  async getTrashItems(): Promise<FileItem[]> {
    const response = await this.api.get('/files/trashed')
    return response.data
  }

  // Secure file URLs
  async generateSecureFileUrl(fileId: number, type: 'download' | 'preview'): Promise<string> {
    const response = await this.api.post(`/files/${fileId}/secure-url`, { type })
    return response.data.url
  }

  getFileDownloadUrl(fileId: number): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
    const token = localStorage.getItem('auth_token')
    return `${baseUrl}/files/${fileId}/download?token=${token}`
  }

  getFilePreviewUrl(fileId: number): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
    const token = localStorage.getItem('auth_token')
    return `${baseUrl}/files/${fileId}/preview?token=${token}`
  }

  getFolderDownloadZipUrl(folderId: number): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
    const token = localStorage.getItem('auth_token')
    return `${baseUrl}/folders/${folderId}/download-zip?token=${token}`
  }

  // Recently Viewed
  async trackFileView(fileId: number): Promise<void> {
    await this.api.post('/recently-viewed/track', { file_id: fileId })
  }

  async getRecentlyViewedFiles(limit = 10): Promise<RecentlyViewedRecord[]> {
    const response = await this.api.get('/recently-viewed', { params: { limit } })
    return response.data
  }

  async clearRecentlyViewed(): Promise<void> {
    await this.api.delete('/recently-viewed/clear')
  }

  async removeFileFromRecentlyViewed(fileId: number): Promise<void> {
    await this.api.delete('/recently-viewed/remove', { data: { file_id: fileId } })
  }

  // Favorites
  async getFavorites(): Promise<Favorite[]> {
    const response = await this.api.get('/favorites')
    return response.data
  }

  async addFavorite(type: 'file' | 'folder', id: number): Promise<Favorite> {
    const response = await this.api.post('/favorites', { type, id })
    return response.data
  }

  async removeFavorite(type: 'file' | 'folder', id: number): Promise<void> {
    await this.api.delete('/favorites', { data: { type, id } })
  }

  // Chat/AI Assistant
  async chatQuery(data: {
    question: string
    folder_id?: number
    file_id?: number
    session_id?: string
    conversation_history?: Array<{ type: 'user' | 'assistant'; content: string }>
  }): Promise<{ answer: string; suggested_questions: string[]; sources?: ChatSource[] }> {
    const response = await this.api.post('/chat/query', data, {
      timeout: 60000,
    })
    return response.data
  }

  /**
   * Ask the chatbot and receive the answer as server-sent events (POST /chat/stream).
   * `onEvent` gets each event as it arrives: meta, delta, sources, suggestions, done or error.
   */
  async streamChat(
    data: ChatStreamBody,
    onEvent: (event: string, payload: any) => void,
    signal: AbortSignal
  ): Promise<void> {
    return streamChat(data, onEvent, signal)
  }

  async getChatHistory(
    sessionId: string
  ): Promise<{ messages: Array<{ message: string; response: string; created_at: string }> }> {
    const response = await this.api.get(`/chat/history/${sessionId}`)
    return response.data
  }

  async clearChatHistory(sessionId: string): Promise<void> {
    await this.api.delete(`/chat/history/${sessionId}`)
  }

  // File Notes
  async getFileNotes(fileId: number): Promise<FileNotesResponse> {
    const response = await this.api.get(`/files/${fileId}/notes`)
    return response.data
  }

  async createFileNote(
    fileId: number,
    data: { content: string; visibility: FileNoteVisibility }
  ): Promise<{ note: FileNote }> {
    const response = await this.api.post(`/files/${fileId}/notes`, data)
    return response.data
  }

  async updateFileNote(
    fileId: number,
    noteId: number,
    data: { content: string; visibility: FileNoteVisibility }
  ): Promise<{ note: FileNote }> {
    const response = await this.api.put(`/files/${fileId}/notes/${noteId}`, data)
    return response.data
  }

  async deleteFileNote(fileId: number, noteId: number): Promise<void> {
    await this.api.delete(`/files/${fileId}/notes/${noteId}`)
  }

  // Audit Log
  async trackNavigation(
    action: 'navigation.recently_viewed' | 'navigation.favorites' | 'navigation.shared_with_me'
  ): Promise<void> {
    await this.api.post('/audit/track', { action })
  }

  async getAuditLog(filters: AuditLogFilters, page = 1): Promise<AuditLogPaginated> {
    const params: Record<string, any> = { page }
    if (filters.date_from) params.date_from = filters.date_from
    if (filters.date_to) params.date_to = filters.date_to
    if (filters.action) params.action = filters.action
    if (filters.per_page) params.per_page = filters.per_page
    const response = await this.api.get('/audit', { params })
    return response.data
  }

  async getUserAuditLog(
    userId: number,
    filters: AuditLogFilters,
    page = 1
  ): Promise<AuditLogPaginated> {
    const params: Record<string, any> = { page }
    if (filters.date_from) params.date_from = filters.date_from
    if (filters.date_to) params.date_to = filters.date_to
    if (filters.action) params.action = filters.action
    if (filters.per_page) params.per_page = filters.per_page
    const response = await this.api.get(`/organization/users/${userId}/audit`, { params })
    return response.data
  }

  async getOrganizationUsers(): Promise<
    { id: number; name: string; email: string; role: string }[]
  > {
    const response = await this.api.get('/organization/users')
    return response.data.users
  }

  // Organization statistics
  async getOrganizationStats(): Promise<{
    total_file_size: string
    total_page_count: string
    total_files: number
    file_types: Record<string, { count: number; size: string }>
    ocr_status: Record<string, number>
    embedding_status: Record<string, number>
    calculated_at: string | null
    page_limits: {
      total_available: number
      current_used: number
      remaining: number
      usage_percentage: number
      is_at_limit: boolean
      plan_name: string
    }
  }> {
    const response = await this.api.get('/organization/stats')
    return response.data
  }

  // Current user profile
  async getMe(): Promise<{ user: User }> {
    const response = await this.api.get('/me')
    return response.data
  }

  async updateMe(data: { name?: string; locale?: string | null }): Promise<{ user: User }> {
    const response = await this.api.put('/me', data)
    return response.data
  }

  // Usage statistics
  async getUsageSummary(scope: 'user' | 'org' = 'org'): Promise<UsageSummary> {
    const response = await this.api.get('/usage/summary', { params: { scope } })
    return response.data
  }

  async getUsageTimeseries(
    params: {
      features?: UsageFeatureKey[]
      from?: string
      to?: string
      scope?: 'user' | 'org'
    } = {}
  ): Promise<UsageTimeseries> {
    const query: Record<string, string> = {}
    if (params.features?.length) query.features = params.features.join(',')
    if (params.from) query.from = params.from
    if (params.to) query.to = params.to
    if (params.scope) query.scope = params.scope

    const response = await this.api.get('/usage/timeseries', { params: query })
    return response.data
  }

  // Admin Organization Management
  async getAdminOrganizations(): Promise<Organization[]> {
    const response = await this.api.get('/admin/organizations')
    return response.data.organizations
  }

  async createAdminOrganization(data: {
    name: string
    description?: string
    admin_name: string
    admin_email: string
    admin_password: string
    settings?: Record<string, any>
    plan_id?: number
    access_expires_at?: string | null
  }): Promise<Organization> {
    const response = await this.api.post('/admin/organizations', data)
    return response.data
  }

  async getAdminOrganization(id: number): Promise<Organization> {
    const response = await this.api.get(`/admin/organizations/${id}`)
    return response.data.organization
  }

  async updateAdminOrganization(
    id: number,
    data: {
      name?: string
      description?: string
      active?: boolean
      language?: 'en' | 'es' | null
      settings?: Record<string, any>
      access_expires_at?: string | null
    }
  ): Promise<Organization> {
    const response = await this.api.put(`/admin/organizations/${id}`, data)
    return response.data
  }

  async deleteAdminOrganization(id: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/admin/organizations/${id}`)
    return response.data
  }

  async inviteUserToOrganization(
    organizationId: number,
    data: {
      email: string
      role: 'admin' | 'editor' | 'viewer'
    }
  ): Promise<{ message: string }> {
    const response = await this.api.post(`/admin/organizations/${organizationId}/invite`, data)
    return response.data
  }

  async setOrganizationUsageLimits(
    organizationId: number,
    limits: Record<string, any>
  ): Promise<{ message: string }> {
    const response = await this.api.post(
      `/admin/organizations/${organizationId}/usage-limits`,
      limits
    )
    return response.data
  }

  async toggleOrganizationStatus(
    organizationId: number
  ): Promise<{ organization: Organization; message: string }> {
    const response = await this.api.patch(`/admin/organizations/${organizationId}/status`)
    return response.data
  }

  async toggleUserStatus(
    organizationId: number,
    userId: number
  ): Promise<{
    user: { id: number; name: string; email: string; role: string; is_active: boolean }
    message: string
  }> {
    const response = await this.api.patch(
      `/admin/organizations/${organizationId}/users/${userId}/toggle-status`
    )
    return response.data
  }

  async addUserToOrganization(
    organizationId: number,
    data: { name: string; email: string; password: string; role: string }
  ): Promise<{ user: { id: number; name: string; email: string; role: string }; message: string }> {
    const response = await this.api.post(`/admin/organizations/${organizationId}/users`, data)
    return response.data
  }

  async resetUserPassword(
    organizationId: number,
    userId: number
  ): Promise<{ temporary_password: string; message: string }> {
    const response = await this.api.post(
      `/admin/organizations/${organizationId}/users/${userId}/reset-password`
    )
    return response.data
  }

  // Organization Invitation Management
  async getOrganizationInvitations(): Promise<
    Array<{
      id: number
      email: string
      role: string
      status: 'pending' | 'accepted' | 'expired'
      expires_at: string
      created_at: string
    }>
  > {
    const response = await this.api.get('/organization/invitations')
    return response.data.invitations
  }

  async sendOrganizationInvitation(data: {
    email: string
    role: 'admin' | 'editor' | 'viewer'
  }): Promise<{ message: string }> {
    const response = await this.api.post('/organization/invitations', data)
    return response.data
  }

  async cancelOrganizationInvitation(invitationId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/organization/invitations/${invitationId}`)
    return response.data
  }

  // Invitation Acceptance (Public routes)
  async getInvitation(token: string): Promise<{
    id: number
    email: string
    role: string
    organization: { id: number; name: string; description?: string }
    expires_at: string
    status: 'pending' | 'accepted' | 'expired'
  }> {
    const response = await this.api.get(`/invitations/${token}`)
    return response.data
  }

  async acceptInvitation(
    token: string,
    data: {
      name: string
      password: string
    }
  ): Promise<{
    user: User
    token: string
    organization: Organization
    message: string
  }> {
    const response = await this.api.post(`/invitations/${token}/accept`, data)
    return response.data
  }

  // Plan Management API
  async getAllPlans(): Promise<any[]> {
    const response = await this.api.get('/admin/plans')
    return response.data.plans
  }

  async createPlan(data: any): Promise<any> {
    const response = await this.api.post('/admin/plans', data)
    return response.data
  }

  async createCustomPlan(data: {
    name: string
    description?: string
    file_limit: number
    page_limit: number
    smart_tagging_limit: number
    smart_renaming_limit: number
    chatbot_queries_limit: number
    users_limit?: number
    storage_limit_gb?: number
    organization_id?: number
  }): Promise<any> {
    const response = await this.api.post('/admin/plans/custom', data)
    return response.data
  }

  async updatePlan(planId: number, data: any): Promise<any> {
    const response = await this.api.put(`/admin/plans/${planId}`, data)
    return response.data
  }

  async deletePlan(planId: number): Promise<any> {
    const response = await this.api.delete(`/admin/plans/${planId}`)
    return response.data
  }

  async assignPlanToOrganization(
    planId: number,
    organizationId: number,
    reason?: string
  ): Promise<any> {
    const response = await this.api.post(`/admin/plans/${planId}/assign`, {
      organization_id: organizationId,
      reason,
    })
    return response.data
  }

  // Folder permissions
  async getFolderPermissions(folderId: number): Promise<FolderPermission[]> {
    const response = await this.api.get(`/folders/${folderId}/permissions`)
    return response.data
  }

  async grantFolderPermission(
    folderId: number,
    data: { user_id?: number; group_id?: number; permission: PermissionLevel }
  ): Promise<FolderPermission> {
    const response = await this.api.post(`/folders/${folderId}/permissions`, data)
    return response.data
  }

  async revokeFolderPermission(
    folderId: number,
    data: { user_id?: number; group_id?: number }
  ): Promise<void> {
    await this.api.delete(`/folders/${folderId}/permissions`, { data })
  }

  // File permissions
  async getFilePermissions(fileId: number): Promise<FilePermission[]> {
    const response = await this.api.get(`/files/${fileId}/permissions`)
    return response.data
  }

  async grantFilePermission(
    fileId: number,
    data: { user_id?: number; group_id?: number; permission: string }
  ): Promise<FilePermission> {
    const response = await this.api.post(`/files/${fileId}/permissions`, data)
    return response.data
  }

  async revokeFilePermission(
    fileId: number,
    data: { user_id?: number; group_id?: number }
  ): Promise<void> {
    await this.api.delete(`/files/${fileId}/permissions`, { data })
  }

  async getSharedWithMe(): Promise<FileItem[]> {
    const response = await this.api.get('/files/shared-with-me')
    return response.data
  }

  // Groups
  async getGroups(): Promise<Group[]> {
    const response = await this.api.get('/groups')
    return response.data
  }

  async createGroup(data: { name: string }): Promise<Group> {
    const response = await this.api.post('/groups', data)
    return response.data
  }

  async updateGroup(id: number, data: { name: string }): Promise<Group> {
    const response = await this.api.put(`/groups/${id}`, data)
    return response.data
  }

  async deleteGroup(id: number): Promise<void> {
    await this.api.delete(`/groups/${id}`)
  }

  async getGroupMembers(groupId: number): Promise<GroupMember[]> {
    const response = await this.api.get(`/groups/${groupId}/members`)
    return response.data
  }

  async addGroupMember(groupId: number, data: { user_id: number }): Promise<GroupMember> {
    const response = await this.api.post(`/groups/${groupId}/members`, data)
    return response.data
  }

  async removeGroupMember(groupId: number, userId: number): Promise<void> {
    await this.api.delete(`/groups/${groupId}/members/${userId}`)
  }

  // Dashboard metrics
  async getDashboardMetrics(params?: {
    startDate?: string
    endDate?: string
    organizationId?: string
  }): Promise<{
    pagesPerDay: Array<{ date: string; pages: number }>
    filesByType: Array<{ type: string; count: number; color: string }>
    chatbotQueriesPerDay: Array<{ date: string; queries: number }>
    smartTaggingPerDay: Array<{ date: string; tagging: number; renaming: number }>
    organizations: Array<{ id: number; name: string }>
  }> {
    const queryParams = new URLSearchParams()
    if (params?.startDate) queryParams.append('start_date', params.startDate)
    if (params?.endDate) queryParams.append('end_date', params.endDate)
    if (params?.organizationId && params.organizationId !== 'all') {
      queryParams.append('organization_id', params.organizationId)
    }

    const response = await this.api.get(`/admin/dashboard/metrics?${queryParams.toString()}`)
    return response.data
  }
}

export const apiService = new ApiService()
export default apiService
